import React, { useEffect, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { useAppSelector } from "@crm-src/lib/store"
import usePermissions from "@crm-src/hooks/usePermissions"
import { Shield, Lock } from "lucide-react"
import { BackButton } from "@/components/ui/back-button"

interface PagePermissionGuardProps {
  children: React.ReactNode
  requiredPermission: string
  fallback?: React.ReactNode
  redirectTo?: string
}

export function PagePermissionGuard({ 
  children, 
  requiredPermission,
  fallback,
  // `/crm` (not a hard `/crm/tasks`) so the access-denied "Dashboard" button
  // routes through CrmLandingRedirect — it sends the user to THEIR primary CRM
  // page (AI Prospects when they have it, else the first page they can open)
  // instead of dead-ending on Tasks, which some roles can't access.
  redirectTo = "/crm"
}: PagePermissionGuardProps) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const user = useAppSelector((state) => state.auth.user)
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated)
  const isLoading = useAppSelector((state) => state.auth.isLoading)
  const lastFetched = useAppSelector((state) => state.auth.lastFetched)
  const permissions = useAppSelector((state) => state.auth.permissions)
  
  const { canAccessPage } = usePermissions()
  
  const [isChecking, setIsChecking] = useState(true)
  const [accessDenied, setAccessDenied] = useState(false)
  const [denialReason, setDenialReason] = useState("")

  // Check if user has required permission
  const hasRequiredPermission = () => {
    if (!isAuthenticated || !user) {
      setDenialReason("User not authenticated")
      return false
    }

    // Check if user is active and email verified
    if (!user.active) {
      setDenialReason("User account is inactive")
      return false
    }

    if (!user.email_verified) {
      setDenialReason("Email not verified")
      return false
    }
    
    // Special case: Admin users should have access to all pages
    // This handles cases where permissions might not be cached yet
    const hasAdminRole = user.roles && (user.roles as any[]).some((roleName: string) =>
      roleName === 'admin' || roleName === 'Admin'
    )
    const hasAdminPermission = permissions && permissions.includes('admin:access')
    
    if (hasAdminRole || hasAdminPermission) {
      return true
    }
    
    // Check if user has the required permission
    if (!canAccessPage(requiredPermission)) {
      setDenialReason(`Missing required permission: ${requiredPermission}`)
      return false
    }

    return true
  }

  // Security check effect
  useEffect(() => {
    const performSecurityCheck = async () => {
      // If auth system is still loading, wait for it
      if (isLoading) {
        setIsChecking(true)
        return
      }

      // If we have no authentication data at all and haven't tried to fetch yet, wait
      if (!user && !lastFetched && !isAuthenticated) {
        setIsChecking(true)
        return
      }

      // NOTE: we deliberately do NOT wait here for "permissions to load".
      // In the dashboard-integrated CRM, CrmRoot dispatches `setAuthData`
      // ATOMICALLY (user + permissions together) — there is no separate async
      // permissions fetch, so empty permissions is a FINAL answer, not a
      // transient. The old 5s "grace period" early-returned without ever
      // re-running this effect, which spun "Verifying access…" FOREVER for any
      // session minted with no page permissions (e.g. a sales_manager). Always
      // proceed to a definite access decision instead.

      setIsChecking(true)
      
      // Add small delay to prevent flickering
      await new Promise(resolve => setTimeout(resolve, 100))

      // Perform permission check
      const hasAccess = hasRequiredPermission()
      
      if (!hasAccess) {
        console.warn(`🚫 [${requiredPermission}] Page access denied to ${pathname}: ${denialReason}`)
        setAccessDenied(true)
        
        // Log security event
        const securityEvent = {
          type: 'PAGE_ACCESS_DENIED',
          path: pathname,
          required_permission: requiredPermission,
          user_id: user?.id,
          user_email: user?.email,
          reason: denialReason,
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent
        }
        
        console.warn('Security Event:', securityEvent)
        
        setIsChecking(false)
        return
      }

      // Access granted
      setAccessDenied(false)
      setIsChecking(false)
    }

    performSecurityCheck()
  }, [pathname, user, permissions, isAuthenticated, isLoading, lastFetched, requiredPermission])

  // Show loading state during security check
  if (isChecking) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "surfaceSoft" }}>
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={48} sx={{ mb: 2, color: "primary.main" }} />
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>Verifying page access permissions...</Typography>
          <Typography sx={{ fontSize: 12, color: "text.disabled", mt: 1 }}>Required: {requiredPermission}</Typography>
        </Box>
      </Box>
    )
  }

  // Show access denied if user doesn't have required permission
  if (accessDenied) {
    if (fallback) {
      return <>{fallback}</>
    }

    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: (t) => alpha(t.palette.error.main, 0.06) }}>
        <Box sx={{ maxWidth: 448, width: "100%", bgcolor: "background.paper", borderRadius: 1, boxShadow: 3, p: 4, textAlign: "center" }}>
          <Box
            sx={{
              mx: "auto",
              display: "flex",
              height: 80,
              width: 80,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              bgcolor: (t) => alpha(t.palette.error.main, 0.12),
              color: "error.main",
              mb: 3,
            }}
          >
            <Lock size={40} />
          </Box>

          <Typography component="h1" sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary", mb: 2 }}>
            Access Denied
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
              You don't have permission to access this page.
            </Typography>
            <Box sx={{ bgcolor: (t) => alpha(t.palette.error.main, 0.06), border: 1, borderColor: (t) => alpha(t.palette.error.main, 0.24), borderRadius: 1, p: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ color: "error.main", mr: 1, display: "flex" }}><Shield size={16} /></Box>
                <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "error.dark" }}>Required permission: {requiredPermission}</Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}>
                Reason: {denialReason}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, fontSize: 12, color: "text.secondary", mb: 3 }}>
            <Typography sx={{ fontSize: 12 }}>• Contact your administrator to request access</Typography>
            <Typography sx={{ fontSize: 12 }}>• Your access attempt has been logged</Typography>
            <Typography sx={{ fontSize: 12 }}>• Path: {pathname}</Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <BackButton label="Go Back" sx={{ flex: 1 }} />
            <Button
              onClick={() => navigate(redirectTo)}
              variant="contained"
              sx={{ flex: 1 }}
            >
              Dashboard
            </Button>
          </Box>
        </Box>
      </Box>
    )
  }

  // If we reach here, user has required permission
  return <>{children}</>
}

// Higher-order component for page-level protection
function withPagePermissionGuard<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredPermission: string,
  options: Omit<PagePermissionGuardProps, 'children' | 'requiredPermission'> = {}
) {
  return function ProtectedPageComponent(props: P) {
    return (
      <PagePermissionGuard requiredPermission={requiredPermission} {...options}>
        <WrappedComponent {...props} />
      </PagePermissionGuard>
    )
  }
}

// Convenience wrappers for specific page types
export const withTasksAccess = <P extends object>(Component: React.ComponentType<P>) => 
  withPagePermissionGuard(Component, 'tasks')

export const withProspectsAccess = <P extends object>(Component: React.ComponentType<P>) => 
  withPagePermissionGuard(Component, 'prospects')

export const withCustomersAccess = <P extends object>(Component: React.ComponentType<P>) => 
  withPagePermissionGuard(Component, 'customers')

export const withContactsAccess = <P extends object>(Component: React.ComponentType<P>) => 
  withPagePermissionGuard(Component, 'contacts')

export const withEmailCampaignsAccess = <P extends object>(Component: React.ComponentType<P>) => 
  withPagePermissionGuard(Component, 'email_campaigns')

export const withFileManagerAccess = <P extends object>(Component: React.ComponentType<P>) =>
  withPagePermissionGuard(Component, 'file_manager')

export const withFinancialMetricsAccess = <P extends object>(Component: React.ComponentType<P>) =>
  withPagePermissionGuard(Component, 'financial_metrics')
